import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import makeStyles from '@mui/styles/makeStyles';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { formatCurrency } from '../../../../components';
import {
  Feature,
  getProductId,
  PlanInterval,
  SubscriptionSource,
} from '../../../../models';
import { fromOrder, fromSubscription, useAppDispatch } from '../../../../store';
import {
  DEFAULT_CURRENCY_FORMAT_OPTIONS,
  PLAN_SUBSCRIPTION_ID_QUERY_PARAM,
} from '../../../../utils/Constants';
import { getQuotaFromPathParam, sendTrackingData, TrackingPayload } from '../../../../utils/utils';
import { getUnitPrice, useGetTrackingCookies, usePlanCartSnapshot } from '../../../../hook';
import { useLaunchDarkly } from '../../../../launchdarkly';
import { useQuery } from '../../../../routes';
import {
  getTargetSelfServeSubscription,
  isSelfServePaidSubscription,
  parseSubscriptionId,
} from '../../../../utils/subscriptionTarget';
import { SelectQuota } from './SelectQuota';
import {
  TRUE_MORE_TESTING_PRODUCT_FEATURES,
  TRUE_BUNDLE_PLAN_OPTIONS,
  TRUE_BUNDLE_PURCHASE_FEATURES,
} from './trueBundlePlans';
import { getManualPurchaseProductCode } from './trueBundleProductCode';

const useStyles = makeStyles(theme => ({
  sectionLabel: {
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: 1,
    color: '#808B9A',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1),
  },
  planCard: {
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    marginBottom: theme.spacing(1.5),
    overflow: 'visible',
    backgroundColor: '#fff',
  },
  selectedPlanCard: {
    borderColor: '#5959EB',
    boxShadow: '0 0 0 3px rgba(89, 89, 235, 0.08)',
  },
  planRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.down('tablet')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
  },
  planMain: {
    flex: 1,
    minWidth: 0,
  },
  planTitleRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  planTitle: {
    fontWeight: 700,
    fontSize: 18,
    color: '#22283C',
  },
  planDescription: {
    color: '#6B7280',
    fontSize: 13,
    lineHeight: 1.45,
  },
  planPromoDescription: {
    color: '#6B7280',
    fontSize: 13,
    lineHeight: 1.45,
    marginTop: theme.spacing(0.75),
  },
  planPromoHighlight: {
    color: '#16865F',
    fontWeight: 800,
  },
  planProducts: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginLeft: theme.spacing(1),
  },
  productIconBadge: {
    display: 'inline-flex',
    cursor: 'help',
  },
  planPrice: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
    minWidth: 136,
    textAlign: 'right',
    [theme.breakpoints.down('tablet')]: {
      justifyContent: 'flex-start',
      minWidth: 0,
      textAlign: 'left',
    },
  },
  priceAmount: {
    color: '#22283C',
    fontWeight: 800,
    fontSize: 18,
  },
  priceHelp: {
    color: '#808B9A',
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  includedFooter: {
    backgroundColor: '#FAFAFB',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
  },
  includedTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  includedTitle: {
    color: '#808B9A',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  includedItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.75),
  },
  includedCheckIcon: {
    color: '#12A877',
    fontSize: 18,
  },
  includedText: {
    color: '#6B7280',
    fontSize: 12,
    lineHeight: 1.5,
  },
  testingPlansSection: {
    marginTop: theme.spacing(3.5),
  },
  testingPlanRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.75),
    padding: theme.spacing(1.625, 2),
    border: '1px solid #ececf4',
    borderRadius: 12,
    backgroundColor: '#fff',
    marginTop: theme.spacing(1.25),
    [theme.breakpoints.down('tablet')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
  },
  testingPlanMain: {
    flex: 1,
    minWidth: 0,
  },
  testingPlanTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  testingPlanTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#16141d',
  },
  testingPlanPrice: {
    fontSize: 13,
    fontWeight: 600,
    color: '#3c3a50',
    whiteSpace: 'nowrap',
  },
  contactSalesRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    width: '100%',
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.75, 2),
    backgroundColor: '#fbfaff',
    border: '1px dashed #d8d6ee',
    borderRadius: 14,
    textAlign: 'left',
    textDecoration: 'none',
    textTransform: 'none',
    '&:hover': {
      borderColor: '#bcb8e6',
      backgroundColor: '#f5f3ff',
      textDecoration: 'none',
    },
  },
  contactSalesIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: '#fff',
    border: '1px solid #ececf4',
    color: '#73718a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  contactSalesTitle: {
    display: 'block',
    fontSize: 13.8,
    fontWeight: 700,
    color: '#16141d',
  },
  contactSalesDesc: {
    display: 'block',
    fontSize: 12.6,
    color: '#73718a',
  },
  contactSalesCta: {
    fontSize: 13,
    fontWeight: 700,
    color: '#5959EB',
    whiteSpace: 'nowrap',
  },
  planInfoTooltip: {
    maxWidth: 456,
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#ffffff',
    color: 'rgb(55, 65, 81)',
    borderRadius: 6,
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
  },
  planInfoTooltipArrow: {
    color: '#ffffff',
  },
  planInfoTooltipTitle: {
    color: '#22283C',
    fontSize: 13,
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  tooltipList: {
    margin: 0,
    paddingLeft: theme.spacing(2.25),
    '& li': {
      color: 'rgb(55, 65, 81)',
      fontSize: 13,
      lineHeight: 1.45,
      paddingLeft: theme.spacing(0.25),
      marginBottom: theme.spacing(0.75),
    },
    '& li::marker': {
      color: '#5959EB',
    },
    '& li:last-child': {
      marginBottom: 0,
    },
  },
}));

const MAX_SEATS = process.env.REACT_APP_TRUE_TEAM_MAX_SEATS;
const CONTACT_SALES_URL = 'https://katalon.com/contact-us#contact-us';
const TRUE_SUBSCRIPTION_FEATURES = [
  Feature.TRUE_TEAM,
  Feature.TRUE_ENTERPRISE,
  Feature.TRUE_PLATFORM_ONLY,
  Feature.TRUE_AUTOMATION,
];

type ProductIconKey = 'studio' | 'cloud' | 'local' | 'insights' | 'testmgmt';

const PRODUCT_ICON_META: Record<ProductIconKey, {
  backgroundColor: string;
  color: string;
}> = {
  studio: {
    backgroundColor: '#e3f6ee',
    color: '#12a877',
  },
  cloud: {
    backgroundColor: '#fdf3e1',
    color: '#bd862a',
  },
  local: {
    backgroundColor: '#ebecf7',
    color: '#5b5fb0',
  },
  insights: {
    backgroundColor: '#fce8f3',
    color: '#d6388a',
  },
  testmgmt: {
    backgroundColor: '#edeefb',
    color: '#7a7aef',
  },
};

const getPlanProductIconKey = (labelId: string): ProductIconKey => {
  if (labelId.includes('studio')) {
    return 'studio';
  }

  if (labelId.includes('cloud')) {
    return 'cloud';
  }

  return 'testmgmt';
};

const ProductGlyph = (
  { iconKey, color, backgroundColor }: {
    iconKey: ProductIconKey;
    color: string;
    backgroundColor: string;
  },
) => {
  const cutoutStrokeProps = {
    stroke: backgroundColor,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 1.8,
  };

  switch (iconKey) {
    case 'studio':
      return (
        <>
          <rect x="2.5" y="4" width="19" height="13" rx="2.6" fill={color} />
          <rect x="10" y="17" width="4" height="2.4" rx="0" fill={color} />
          <rect x="7.5" y="19.2" width="9" height="1.8" rx="0.9" fill={color} />
          <path d="M9 8 6.7 10.5 9 13" fill="none" {...cutoutStrokeProps} strokeWidth={1.7} />
          <path d="M15 8 17.3 10.5 15 13" fill="none" {...cutoutStrokeProps} strokeWidth={1.7} />
        </>
      );
    case 'cloud':
      return (
        <path
          d="M7 19h9.2a4.3 4.3 0 0 0 .55-8.56 5.8 5.8 0 0 0-11.06-1.16A4.1 4.1 0 0 0 7 19z"
          fill={color}
        />
      );
    case 'local':
      return (
        <>
          <rect x="2.5" y="5" width="19" height="14" rx="2.6" fill={color} />
          <path d="M6.5 10.5 9 13l-2.5 2.5" fill="none" {...cutoutStrokeProps} />
          <path d="M12 15.2h5" fill="none" {...cutoutStrokeProps} />
        </>
      );
    case 'insights':
      return (
        <>
          <rect x="4.2" y="12" width="3.4" height="6.5" rx="1" fill={color} />
          <rect x="10.3" y="7.5" width="3.4" height="11" rx="1" fill={color} />
          <rect x="16.4" y="9.8" width="3.4" height="8.7" rx="1" fill={color} />
        </>
      );
    case 'testmgmt':
    default:
      return (
        <>
          <rect x="5" y="4.5" width="14" height="16" rx="3" fill={color} />
          <rect x="8.8" y="3" width="6.4" height="3.4" rx="1.3" fill={color} />
          <path d="M8.6 12.6l2.2 2.2 4.6-5" fill="none" {...cutoutStrokeProps} strokeWidth={2.1} />
        </>
      );
  }
};

const ProductIconBadge = (
  {
    iconKey,
    size = 26,
    glyphSize = Math.round(size * 0.62),
    borderRadius = Math.round(size * 0.28),
  }: {
    iconKey: ProductIconKey;
    size?: number;
    glyphSize?: number;
    borderRadius?: number;
  },
) => {
  const meta = PRODUCT_ICON_META[iconKey];

  return (
    <Box
      component="span"
      sx={{
        alignItems: 'center',
        backgroundColor: meta.backgroundColor,
        borderRadius,
        display: 'inline-flex',
        flex: 'none',
        height: size,
        justifyContent: 'center',
        width: size,
      }}
    >
      <svg width={glyphSize} height={glyphSize} viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
        <ProductGlyph
          iconKey={iconKey}
          color={meta.color}
          backgroundColor={meta.backgroundColor}
        />
      </svg>
    </Box>
  );
};

type TrueTestingPlanOption = {
  type: 'product';
  feature: Feature;
  titleId: string;
  infoId: string;
  iconKey: ProductIconKey;
};

const TRUE_TESTING_PLAN_OPTIONS: TrueTestingPlanOption[] = [
  {
    type: 'product',
    feature: Feature.PER_USER_KSE,
    titleId: 'feature.per_user_kse.product.name',
    infoId: 'page.plan.v2.true.testing.studio.info',
    iconKey: 'studio',
  },
  {
    type: 'product',
    feature: Feature.UNLIMITED_ENGINE,
    titleId: 'feature.unlimited_engine.product.name',
    infoId: 'page.plan.v2.true.testing.runtime.info',
    iconKey: 'local',
  },
  {
    type: 'product',
    feature: Feature.TESTCLOUD_SESSION_CROSS_BROWSER,
    titleId: 'page.plan.v2.true.testing.cross.browser.title',
    infoId: 'page.plan.v2.true.testing.cross.browser.info',
    iconKey: 'cloud',
  },
  {
    type: 'product',
    feature: Feature.TESTCLOUD_SESSION_MOBILE_APP,
    titleId: 'page.plan.v2.true.testing.mobile.title',
    infoId: 'page.plan.v2.true.testing.mobile.info',
    iconKey: 'cloud',
  },
];

export const TrueBundlePlanSelect = (
  { billingInterval }: {
    billingInterval: PlanInterval;
  },
) => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { flags } = useLaunchDarkly();
  const { get } = useQuery();
  const accountId = Number(get('accountId'));
  const moreTestingPlansEnabled = flags?.feTrueBundleMoreTestingPlansEnabled === true;
  const trackingCookies = useGetTrackingCookies();
  const cartSnapshot = usePlanCartSnapshot();
  const automationSeatsParam = getQuotaFromPathParam(Feature.TRUE_AUTOMATION);
  const platformSeatsParam = getQuotaFromPathParam(Feature.TRUE_PLATFORM_ONLY);
  const studioSeatsParam = getQuotaFromPathParam(Feature.PER_USER_KSE);
  const runtimeSeatsParam = getQuotaFromPathParam(Feature.UNLIMITED_ENGINE);
  const crossBrowserSeatsParam = getQuotaFromPathParam(Feature.TESTCLOUD_SESSION_CROSS_BROWSER);
  const mobileAppSeatsParam = getQuotaFromPathParam(Feature.TESTCLOUD_SESSION_MOBILE_APP);
  const targetSubscriptionId = parseSubscriptionId(get(PLAN_SUBSCRIPTION_ID_QUERY_PARAM));
  const selectAccountSubscriptions = useMemo(
    () => fromSubscription.selectByAccountId(accountId),
    [accountId],
  );
  const accountSubscriptions = useSelector(selectAccountSubscriptions);
  const trueSubscriptions = useMemo(
    () => accountSubscriptions.filter(
      sub => sub.source === SubscriptionSource.RECURLY
        && TRUE_SUBSCRIPTION_FEATURES.includes(sub.feature),
    ),
    [accountSubscriptions],
  );
  const recurlySubscriptions = useMemo(
    () => accountSubscriptions.filter(sub => sub.source === SubscriptionSource.RECURLY),
    [accountSubscriptions],
  );
  const selfServeBundleSubscriptions = useMemo(
    () => trueSubscriptions.filter(
      sub => isSelfServePaidSubscription(sub)
        && TRUE_BUNDLE_PURCHASE_FEATURES.includes(sub.feature),
    ),
    [trueSubscriptions],
  );
  const getBundleSubscriptionForFeature = useCallback(
    (feature: Feature) => getTargetSelfServeSubscription(
      selfServeBundleSubscriptions,
      feature,
      targetSubscriptionId,
    ),
    [selfServeBundleSubscriptions, targetSubscriptionId],
  );
  const hasQuotaFromQuery = automationSeatsParam !== undefined || platformSeatsParam !== undefined;
  const syncedSubscriptionRef = useRef(false);
  const [planSeats, setPlanSeats] = useState<Partial<Record<Feature, number>>>({
    [Feature.TRUE_AUTOMATION]: automationSeatsParam ?? 0,
    [Feature.TRUE_PLATFORM_ONLY]: platformSeatsParam ?? 0,
  });
  const [testingProductSeats, setTestingProductSeats] = useState<Partial<Record<Feature, number>>>({
    [Feature.PER_USER_KSE]: studioSeatsParam ?? 0,
    [Feature.UNLIMITED_ENGINE]: runtimeSeatsParam ?? 0,
    [Feature.TESTCLOUD_SESSION_CROSS_BROWSER]: crossBrowserSeatsParam ?? 0,
    [Feature.TESTCLOUD_SESSION_MOBILE_APP]: mobileAppSeatsParam ?? 0,
  });

  useEffect(() => {
    setPlanSeats(current => {
      let next = current;
      const syncQuota = (feature: Feature, quota: number | undefined) => {
        if (quota === undefined || current[feature] === quota) {
          return;
        }
        if (next === current) {
          next = { ...current };
        }
        next[feature] = quota;
      };

      syncQuota(Feature.TRUE_AUTOMATION, automationSeatsParam);
      syncQuota(Feature.TRUE_PLATFORM_ONLY, platformSeatsParam);
      return next;
    });
  }, [automationSeatsParam, platformSeatsParam]);

  useEffect(() => {
    setTestingProductSeats(current => {
      let next = current;
      const syncQuota = (feature: Feature, quota: number | undefined) => {
        if (quota === undefined || current[feature] === quota) {
          return;
        }
        if (next === current) {
          next = { ...current };
        }
        next[feature] = quota;
      };

      syncQuota(Feature.PER_USER_KSE, studioSeatsParam);
      syncQuota(Feature.UNLIMITED_ENGINE, runtimeSeatsParam);
      syncQuota(Feature.TESTCLOUD_SESSION_CROSS_BROWSER, crossBrowserSeatsParam);
      syncQuota(Feature.TESTCLOUD_SESSION_MOBILE_APP, mobileAppSeatsParam);
      return next;
    });
  }, [
    studioSeatsParam,
    runtimeSeatsParam,
    crossBrowserSeatsParam,
    mobileAppSeatsParam,
  ]);

  const getTrackingPayloadForFeature = (
    feature: Feature,
    quantity?: number,
  ): TrackingPayload => ({
    object: Feature.toString(feature),
    product_id: getProductId(feature),
    product_code: getManualPurchaseProductCode(feature, billingInterval),
    billing_interval: PlanInterval.getPlanIntervalCode(billingInterval),
    unit_price: getUnitPrice(
      flags?.subscriptionPlanPrice,
      feature,
      billingInterval,
      'product',
    ),
    ...(quantity !== undefined ? { quantity } : {}),
  });

  const trackPlanPageAction = (payload: TrackingPayload) => {
    if (!accountId) {
      return;
    }

    sendTrackingData(
      'plan_page_all_action',
      accountId,
      undefined,
      {
        field: payload.field,
        object: payload.object,
        action: payload.action,
        plan_flow: 'true_bundle',
        ...payload,
        ...trackingCookies,
        cart_snapshot: cartSnapshot,
      },
    );
  };

  useEffect(() => {
    if (selfServeBundleSubscriptions.length === 0
      || hasQuotaFromQuery
      || syncedSubscriptionRef.current) {
      return;
    }

    syncedSubscriptionRef.current = true;
    setPlanSeats({
      [Feature.TRUE_AUTOMATION]: 0,
      [Feature.TRUE_PLATFORM_ONLY]: 0,
    });
  }, [hasQuotaFromQuery, selfServeBundleSubscriptions]);

  useEffect(() => {
    TRUE_BUNDLE_PLAN_OPTIONS.forEach(plan => {
      const planSubscription = getBundleSubscriptionForFeature(plan.feature);
      const isMonthlyToAnnualSwitch = !!planSubscription?.recurlySubUuId
        && planSubscription.billingInterval === PlanInterval.MONTH
        && billingInterval === PlanInterval.YEAR
        && (planSeats[plan.feature] ?? 0) === 0;
      dispatch(fromOrder.updateProductPreviewRequest({
        product: {
          productCode: getManualPurchaseProductCode(plan.feature, billingInterval),
          quantity: planSeats[plan.feature] ?? 0,
          ...(planSubscription?.recurlySubUuId
            ? { recurlySubscriptionUuid: planSubscription.recurlySubUuId }
            : {}),
        },
        keepZeroQuantity: isMonthlyToAnnualSwitch,
      }));
    });

    const bundleSubscription = targetSubscriptionId
      ? selfServeBundleSubscriptions.find(sub => sub.id === targetSubscriptionId)
      : selfServeBundleSubscriptions[0];
    if (bundleSubscription) {
      const subscribedPlan = TRUE_BUNDLE_PLAN_OPTIONS.find(
        plan => plan.feature === bundleSubscription.feature,
      );
      if (subscribedPlan) {
        dispatch(fromOrder.setTruePlatformPreviewRequest({
          productCode: getManualPurchaseProductCode(subscribedPlan.feature, billingInterval),
          quantity: planSeats[bundleSubscription.feature] ?? 0,
          ...(bundleSubscription.recurlySubUuId
            ? { recurlySubscriptionUuid: bundleSubscription.recurlySubUuId }
            : {}),
        }));
      }
    }
  }, [
    billingInterval,
    dispatch,
    getBundleSubscriptionForFeature,
    planSeats,
    selfServeBundleSubscriptions,
    targetSubscriptionId,
  ]);

  useEffect(() => {
    TRUE_MORE_TESTING_PRODUCT_FEATURES.forEach(feature => {
      const productSubscription = getTargetSelfServeSubscription(
        recurlySubscriptions,
        feature,
        targetSubscriptionId,
      );
      const quantity = moreTestingPlansEnabled ? testingProductSeats[feature] ?? 0 : 0;
      dispatch(fromOrder.updateProductPreviewRequest({
        product: {
          productCode: `${getProductId(feature)}_${PlanInterval.getPlanIntervalCode(billingInterval)}`,
          quantity,
          ...(productSubscription?.recurlySubUuId
            ? { recurlySubscriptionUuid: productSubscription.recurlySubUuId }
            : {}),
        },
      }));
    });
  }, [
    billingInterval,
    dispatch,
    moreTestingPlansEnabled,
    recurlySubscriptions,
    targetSubscriptionId,
    testingProductSeats,
  ]);

  const updatePlanSeats = (feature: Feature) => (quota: number) => {
    const previousQuota = planSeats[feature] ?? 0;
    if (previousQuota !== quota) {
      trackPlanPageAction({
        field: 'true_bundle_plan',
        action: 'change_quantity',
        previous_quantity: previousQuota,
        ...getTrackingPayloadForFeature(feature, quota),
      });
    }

    setPlanSeats(current => ({
      ...current,
      [feature]: quota,
    }));
  };

  const updateTestingProductSeats = (feature: Feature) => (quota: number) => {
    const previousQuota = testingProductSeats[feature] ?? 0;
    if (previousQuota !== quota) {
      trackPlanPageAction({
        field: 'more_testing_plan',
        action: 'change_quantity',
        previous_quantity: previousQuota,
        ...getTrackingPayloadForFeature(feature, quota),
      });
    }

    setTestingProductSeats(current => ({
      ...current,
      [feature]: quota,
    }));
  };

  return (
    <Box id="true-bundle-plan-select" py={2}>
      <Box>
        {TRUE_BUNDLE_PLAN_OPTIONS.map(plan => {
          const seats = planSeats[plan.feature] ?? 0;
          const isCurrentSubscription = selfServeBundleSubscriptions.some(
            sub => sub.feature === plan.feature,
          );
          const isSelected = seats > 0 || isCurrentSubscription;
          const unitPrice = getUnitPrice(
            flags?.subscriptionPlanPrice,
            plan.feature,
            billingInterval,
            'product',
          );

          return (
            <Box
              key={plan.feature}
              className={`${classes.planCard} ${isSelected ? classes.selectedPlanCard : ''}`}
            >
              <Box className={classes.planRow}>
                <Box className={classes.planMain}>
                  <Box className={classes.planTitleRow}>
                    <Typography className={classes.planTitle}>
                      <FormattedMessage id={plan.titleId} />
                    </Typography>
                    <Tooltip
                      arrow
                      disableInteractive
                      placement="right"
                      onOpen={() => trackPlanPageAction({
                        field: 'true_bundle_plan',
                        action: 'view_tooltip',
                        tooltip: 'plan_details',
                        ...getTrackingPayloadForFeature(plan.feature, seats),
                      })}
                      classes={{
                        tooltip: classes.planInfoTooltip,
                        arrow: classes.planInfoTooltipArrow,
                      }}
                      title={(
                        <Box>
                          <Typography className={classes.planInfoTooltipTitle}>
                            {intl.formatMessage({ id: plan.tooltipTitleId })}
                          </Typography>
                          <ul className={classes.tooltipList}>
                            {plan.benefitIds.map(benefitId => (
                              <li key={benefitId}>{intl.formatMessage({ id: benefitId })}</li>
                            ))}
                          </ul>
                        </Box>
                      )}
                    >
                      <InfoOutlinedIcon sx={{ color: '#808B9A', fontSize: 18, cursor: 'help' }} />
                    </Tooltip>
                    <Box className={classes.planProducts}>
                      {plan.productLabelIds.map(labelId => {
                        const productIconKey = getPlanProductIconKey(labelId);
                        const productLabel = intl.formatMessage({ id: labelId });

                        return (
                          <Tooltip
                            arrow
                            disableInteractive
                            key={labelId}
                            title={productLabel}
                            classes={{
                              tooltip: classes.planInfoTooltip,
                              arrow: classes.planInfoTooltipArrow,
                            }}
                            onOpen={() => trackPlanPageAction({
                              field: 'true_bundle_product_icon',
                              object: productLabel,
                              action: 'view_tooltip',
                              parent_object: Feature.toString(plan.feature),
                              label_id: labelId,
                              billing_interval: PlanInterval.getPlanIntervalCode(billingInterval),
                            })}
                          >
                            <Box
                              aria-label={productLabel}
                              className={classes.productIconBadge}
                              component="span"
                              role="img"
                            >
                              <ProductIconBadge iconKey={productIconKey} />
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Box>
                  {plan.feature === Feature.TRUE_AUTOMATION && (
                    <Typography className={classes.planPromoDescription}>
                      <FormattedMessage
                        id="page.plan.v2.true.automation.discount.description"
                        values={{
                          highlight: (chunks: ReactNode) => (
                            <Box component="span" className={classes.planPromoHighlight}>
                              {chunks}
                            </Box>
                          ),
                        }}
                      />
                    </Typography>
                  )}
                </Box>

                <Box className={classes.planPrice}>
                  <Typography className={classes.priceAmount}>
                    {formatCurrency(intl, unitPrice, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
                  </Typography>
                  <Typography className={classes.priceHelp}>
                    <FormattedMessage id={plan.priceHelpId} />
                  </Typography>
                </Box>

                <SelectQuota
                  productId={getProductId(plan.feature)}
                  initQuota={0}
                  quota={seats}
                  setQuota={updatePlanSeats(plan.feature)}
                  maxQuota={MAX_SEATS ? Number(MAX_SEATS) : undefined}
                  width={40}
                  height={40}
                />
              </Box>
            </Box>
          );
        })}

        <Box className={classes.includedFooter}>
          <Box className={classes.includedTitleRow}>
            <Typography className={classes.includedTitle}>
              <FormattedMessage id="page.plan.v2.true.bundle.included.title" />
            </Typography>
          </Box>
          <Box className={classes.includedItem}>
            <CheckIcon className={classes.includedCheckIcon} />
            <Typography className={classes.includedText}>
              <FormattedMessage id="page.plan.v2.true.bundle.included.description" />
            </Typography>
          </Box>
        </Box>
      </Box>

      {moreTestingPlansEnabled && (
      <Box className={classes.testingPlansSection}>
        <Typography className={classes.sectionLabel}>
          <FormattedMessage id="page.plan.v2.true.bundle.more.testing.plans" />
        </Typography>

        {TRUE_TESTING_PLAN_OPTIONS.map(option => {
          const quota = testingProductSeats[option.feature] ?? 0;
          const unitPrice = getUnitPrice(
            flags?.subscriptionPlanPrice,
            option.feature,
            billingInterval,
            'product',
          );

          return (
            <Box
              className={classes.testingPlanRow}
              key={option.feature}
            >
              <ProductIconBadge
                iconKey={option.iconKey}
                size={28}
                glyphSize={17}
                borderRadius={8}
              />
              <Box className={classes.testingPlanMain}>
                <Box className={classes.testingPlanTitleRow}>
                  <Typography className={classes.testingPlanTitle}>
                    <FormattedMessage id={option.titleId} />
                  </Typography>
                  <Tooltip
                    arrow
                    disableInteractive
                    title={intl.formatMessage({ id: option.infoId })}
                    classes={{
                      tooltip: classes.planInfoTooltip,
                      arrow: classes.planInfoTooltipArrow,
                    }}
                    onOpen={() => trackPlanPageAction({
                      field: 'more_testing_plan',
                      action: 'view_tooltip',
                      tooltip: 'plan_details',
                      ...getTrackingPayloadForFeature(option.feature, quota),
                    })}
                  >
                    <InfoOutlinedIcon sx={{ color: '#9a98ad', fontSize: 16, cursor: 'help' }} />
                  </Tooltip>
                </Box>
              </Box>
              <Typography className={classes.testingPlanPrice}>
                {formatCurrency(intl, unitPrice, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
                <FormattedMessage
                  id={billingInterval === PlanInterval.YEAR
                    ? 'page.plan.v2.true.testing.price.year'
                    : 'page.plan.v2.true.testing.price.month'}
                />
              </Typography>
              <SelectQuota
                productId={getProductId(option.feature)}
                initQuota={0}
                quota={quota}
                setQuota={updateTestingProductSeats(option.feature)}
                width={38}
                height={40}
              />
            </Box>
          );
        })}

        <Button
          className={classes.contactSalesRow}
          component="a"
          href={CONTACT_SALES_URL}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackPlanPageAction({
            field: 'contact_sales',
            object: 'talk_to_sales',
            action: 'click',
            url: CONTACT_SALES_URL,
          })}
        >
          <Box className={classes.contactSalesIcon}>
            <LockOutlinedIcon fontSize="small" />
          </Box>
          <Box flex={1}>
            <Typography className={classes.contactSalesTitle}>
              <FormattedMessage id="page.plan.v2.true.bundle.contact.sales.title" />
            </Typography>
            <Typography className={classes.contactSalesDesc}>
              <FormattedMessage id="page.plan.v2.true.bundle.contact.sales.description" />
            </Typography>
          </Box>
          <Typography className={classes.contactSalesCta}>
            <FormattedMessage id="page.plan.v2.true.bundle.contact.sales.cta" />
          </Typography>
        </Button>
      </Box>
      )}
    </Box>
  );
};
